/**
 * Where each dashboard keeps a tour's steps.
 *
 * Tours have no step authoring in the BFF: the steps live in the dashboard's own code, next to the
 * page component. The BFF only decides who gets the tour and at which revision. The editor shows
 * this registry entry so whoever is editing knows where the content actually is.
 *
 * Mirrored by hand from each dashboard's `src/BFF/tours.ts` (`TOUR_PAGES`). Only
 * dashboard-utilities has a registry today; the other brands return nothing, and the editor simply
 * omits the reference block.
 *
 * @module Shared/brand
 */

import type { Brand } from './pageCatalog';

/** A dashboard's tour entry, keyed by the BFF page slug it belongs to. */
export interface TourRegistryEntry {
  /** The `TOUR_PAGES` key in the dashboard's own code. */
  registryKey: string;
  /** localStorage key gating whether this user has already seen the tour. */
  localKey: string;
}

const UTILITIES_TOURS: Record<string, TourRegistryEntry> = {
  reports: { registryKey: 'reports', localKey: 'reports-tour-welcome-seen-v1' },
  groups: { registryKey: 'groups', localKey: 'groups-tour-welcome-seen-v1' },
  projections: { registryKey: 'projections', localKey: 'projections-tour-welcome-seen-v1' },
  coverage: { registryKey: 'h3', localKey: 'h3-tour-welcome-seen-v1' },
  alarms: { registryKey: 'alarms', localKey: 'alarms-tour-welcome-seen-v1' },
  'alarms-severity': {
    registryKey: 'alarms-severity',
    localKey: 'alarms-severity-tour-welcome-seen-v1',
  },
  'alarms-config': { registryKey: 'alarms-config', localKey: 'alarms-config-tour-welcome-seen-v1' },
  'device-list': { registryKey: 'device-list', localKey: 'device-list-tour-welcome-seen-v1' },
  'health-check': { registryKey: 'health-check', localKey: 'health-check-tour-welcome-seen-v1' },
  tags: { registryKey: 'tags', localKey: 'tags-tour-welcome-seen-v1' },
};

const REGISTRY_BY_BRAND: Record<Brand, Record<string, TourRegistryEntry>> = {
  utilities: UTILITIES_TOURS,
  // No tour registry in these dashboards yet.
  dataoris: {},
  sonarprint: {},
};

/**
 * The registry entry for a page slug, or `undefined` when that dashboard has no tour wired for it.
 *
 * A page can have a tour record in the BFF without an entry here — that means the BFF would serve
 * a tour the dashboard has no steps for, which is worth surfacing rather than hiding.
 */
export function getTourRegistryEntry(
  brand: Brand,
  pageSlug: string,
): TourRegistryEntry | undefined {
  return REGISTRY_BY_BRAND[brand][pageSlug];
}
